import React, { useCallback, useRef, useState } from 'react';
import { COLUMN_TYPES, CELL_OVERFLOW_LENGTH } from './utils/constants';
import { isImageUrl, formatNumber } from './utils/formatters';
import { copyText } from '../../utils/clipboard';

function TableCell({
    value,
    column,
    row,
    onImageClick,
    onTextOverflow,
    stickyLeft,
    conditionalClass,
}) {
    const [showCopy, setShowCopy] = useState(false);
    const tdRef = useRef(null);

    const handleCopy = useCallback(
        async (e) => {
            e.stopPropagation();
            await copyText(value);
        },
        [value]
    );

    const handleOverflowClick = useCallback(
        (e) => {
            e.stopPropagation();
            if (onTextOverflow && typeof value === 'string' && value.length > CELL_OVERFLOW_LENGTH) {
                onTextOverflow(value);
            }
        },
        [value, onTextOverflow]
    );

    const renderContent = () => {
        const type = column.type || COLUMN_TYPES.TEXT;

        if (column.render) {
            return column.render(value, row);
        }

        if (value == null || value === '') {
            return <span className="ut-cell-empty">-</span>;
        }

        switch (type) {
            case COLUMN_TYPES.IMAGE: {
                const url = String(value);
                if (!url || !isImageUrl(url)) {
                    return <span className="ut-cell-empty">-</span>;
                }
                const size = column.imageSize || 40;
                return (
                    <img
                        src={url}
                        alt="preview"
                        className="ut-cell-image"
                        style={{ width: size, height: size }}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onImageClick) onImageClick(url);
                        }}
                        loading="lazy"
                        onError={(e) => {
                            e.target.style.display = 'none';
                        }}
                    />
                );
            }

            case COLUMN_TYPES.NUMBER: {
                const formatted = column.format
                    ? column.format(value)
                    : formatNumber(value);
                return <span className="ut-cell-number">{formatted}</span>;
            }

            case COLUMN_TYPES.DATE: {
                const formatted = column.format
                    ? column.format(value)
                    : String(value);
                return <span>{formatted}</span>;
            }

            case COLUMN_TYPES.TEXT:
            default: {
                const str = String(value);
                const isOverflow = str.length > CELL_OVERFLOW_LENGTH;
                const formatted = column.format ? column.format(value) : str;
                return (
                    <span
                        className={isOverflow ? 'ut-cell-overflow' : ''}
                        onClick={isOverflow ? handleOverflowClick : undefined}
                        title={isOverflow ? str : undefined}
                    >
                        {isOverflow ? formatted.slice(0, CELL_OVERFLOW_LENGTH) + '...' : formatted}
                    </span>
                );
            }
        }
    };

    const isSticky = stickyLeft != null;
    const align = column.align || 'left';
    const className = [
        'ut-td',
        isSticky ? 'ut-td--sticky' : '',
        conditionalClass || '',
        align !== 'left' ? `ut-td--${align}` : '',
    ].filter(Boolean).join(' ');

    const style = {};
    if (column.width) {
        style.width = column.width;
        style.minWidth = column.minWidth || column.width;
    }
    if (isSticky) {
        style.left = stickyLeft;
    }

    return (
        <td
            ref={tdRef}
            className={className}
            style={style}
            onMouseEnter={() => setShowCopy(true)}
            onMouseLeave={() => setShowCopy(false)}
        >
            <div className="ut-cell-wrap">
                {renderContent()}
                {showCopy && value != null && value !== '' && (
                    <button
                        type="button"
                        className="ut-cell-copy"
                        onClick={handleCopy}
                        title="Скопировать"
                        aria-label="Скопировать значение"
                    >
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M4 2a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H6z" />
                            <path d="M2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-1H8v1H2V6h1V5H2z" />
                        </svg>
                    </button>
                )}
            </div>
        </td>
    );
}

export default React.memo(TableCell);
